import { useEffect, useEffectEvent } from 'react'
import { AppState, Platform } from 'react-native'

import {
  buildAppOpenProperties,
  initAttribution,
  initMixpanel,
  MixpanelEvents,
  registerSuperProperties,
  trackEvent,
} from '../services/analytics'

interface AppLifecycleAnalyticsOptions {
  enabled: boolean
  appVersion: string
  buildNumber: string
  environment: string
  flavour: string
  locale: string
}

function generateSessionId(): string {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return globalThis.crypto.randomUUID()
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function useAppLifecycleAnalytics({
  enabled,
  appVersion,
  buildNumber,
  environment,
  flavour,
  locale,
}: AppLifecycleAnalyticsOptions): void {
  const buildLifecycleProperties = useEffectEvent(() =>
    buildAppOpenProperties({
      platform: Platform.OS,
      appVersion,
      buildNumber,
      locale,
      flavour,
      environment,
    }),
  )
  const buildSuperProperties = useEffectEvent(() => ({
    platform: Platform.OS,
    app_version: appVersion,
    session_id: generateSessionId(),
  }))

  useEffect(() => {
    if (!enabled) return

    let isCancelled = false
    let removeAppStateListener: (() => void) | undefined

    const initializeAnalytics = async () => {
      await initMixpanel()
      if (isCancelled) return

      registerSuperProperties(buildSuperProperties())
      trackEvent(MixpanelEvents.AppOpened, buildLifecycleProperties())

      if (Platform.OS !== 'web') {
        let hasBeenActive = AppState.currentState === 'active'
        let hasLeftActiveState = false

        const subscription = AppState.addEventListener(
          'change',
          (nextState) => {
            if (nextState === 'active') {
              if (hasBeenActive && hasLeftActiveState) {
                trackEvent(
                  MixpanelEvents.AppResumed,
                  buildLifecycleProperties(),
                )
              }
              hasBeenActive = true
              hasLeftActiveState = false
              return
            }

            if (
              hasBeenActive &&
              (nextState === 'background' || nextState === 'inactive')
            ) {
              hasLeftActiveState = true
            }
          },
        )
        removeAppStateListener = () => subscription.remove()
      }

      await initAttribution()
    }

    void initializeAnalytics()

    return () => {
      isCancelled = true
      removeAppStateListener?.()
    }
  }, [enabled])
}
